<script setup lang="ts">
import { ref } from 'vue';
import { useForm } from 'vee-validate';
import { toTypedSchema } from '@vee-validate/zod';
import { useConfirm } from 'primevue/useconfirm';
import ConfirmDialog from 'primevue/confirmdialog';
import {
  useContactDetails,
  useToastNotifications,
} from '@apparatix/composables';
import { ContactNotes } from '@apparatix/schemas/ContactNote.ts';
import { useContactStore } from '@apparatix/stores/useContactStore.ts';
import ContactNoteEditorForm from '@apparatix/leads/ContactNoteEditorForm.vue';
import ContactNoteViewForm from '@apparatix/leads/ContactNoteViewForm.vue';
import DetailEditorControls from '@apparatix/library/DetailEditorControls.vue';

const store = useContactStore();
const confirm = useConfirm();
const { showSuccess } = useToastNotifications();

const {
  contactNotesSchema,
} = ContactNotes();

const {
  defineField, meta, resetForm, errors: contactNoteEditorErrors, values: contactNoteEditorModel,
} = useForm({
  validationSchema: toTypedSchema(contactNotesSchema),
  initialValues: {
    id: store.contactDetails.id,
    notes: store.contactDetails.notes,
  },
});

const detail = useContactDetails();

const [notes] = defineField('notes');

const isEditing = ref(false);

const onSave = async () => {
  await detail.sendContactUpdate(contactNoteEditorModel);
  showSuccess('Notes Saved', 'Success');
  isEditing.value = false;
  resetForm({
    values: {
      notes: store.contactDetails.notes,
    },
  });
};

const onCancelEditing = () => {
  if (meta.value.dirty) {
    confirm.require({
      header: ' ',
      group: 'confirmNotesClose',
      message: 'You have unsaved changes. Do you want to continue?',
      accept: () => onConfirmCancel(),
      acceptLabel: 'Yes',
      rejectLabel: 'No',
      rejectClass: 'p-button-secondary',
    });
    return;
  }
  onConfirmCancel();
};

const onConfirmCancel = () => {
  resetForm();
  isEditing.value = false;
};

</script>
<template>
  <DetailEditorControls
    header="Notes"
    :is-editing="isEditing"
    :disable-save="Boolean(Object.keys(contactNoteEditorErrors).length) || !meta.dirty"
    @on:edit-click="isEditing = true"
    @on:cancel-click="onCancelEditing"
    @on:save-click="onSave"
  >
    <ContactNoteViewForm
      v-if="!isEditing"
      :contact-notes="contactNoteEditorModel"
      :errors="contactNoteEditorErrors"
    />
    <ContactNoteEditorForm
      v-if="isEditing"
      v-model="contactNoteEditorModel"
      v-model:contact-notes="notes"
      :errors="contactNoteEditorErrors"
    />
  </DetailEditorControls>
  <ConfirmDialog group="confirmNotesClose" />
</template>
